import React from 'react';
import { ATSResumeJson } from '@/types/resume';
import { Loader2, ZoomIn, ZoomOut } from 'lucide-react';
import { Document, Page } from 'react-pdf';
import { Editor } from '@/components/ai-tools/Editor';

interface ResumeContentProps {
  containerRef: React.RefObject<HTMLDivElement>;
  containerWidth: number;
  isEditingJson: boolean;
  editingData: ATSResumeJson | null;
  setEditingData: (data: ATSResumeJson) => void;
  isGenerating: boolean;
  pdfBlob: Blob | null;
  zoom: number;
  setZoom: React.Dispatch<React.SetStateAction<number>>;
  setNumPages: React.Dispatch<React.SetStateAction<number>>;
  numPages: number;
  resumeData?: any;
}

export const ResumeContent = ({
  containerRef,
  containerWidth,
  isEditingJson,
  editingData,
  setEditingData,
  isGenerating,
  pdfBlob,
  zoom,
  setZoom,
  setNumPages,
  numPages,
  resumeData,
}: ResumeContentProps) => {
  return (
    <div ref={containerRef} className="flex-1 relative flex flex-col p-2 md:p-4 overflow-auto rounded-bl-xl rounded-br-xl md:rounded-br-none">
      {isEditingJson ? (
        <div className="flex-1 flex flex-col w-full h-full relative overflow-y-auto">
          {editingData && (
            <Editor
              data={editingData}
              onChange={setEditingData}
            />
          )}
        </div>
      ) : (
        <>
          {pdfBlob && !isGenerating && (
            <div className="sticky top-0 z-50 flex items-center gap-2 mb-4 bg-white/90 dark:bg-slate-900/90 backdrop-blur px-3 py-1.5 rounded-full dark:shadow-sm border border-slate-200 dark:border-slate-700 mx-auto w-fit">
              <button
                onClick={() => setZoom(z => Math.max(0.5, z - 0.25))}
                className="p-1 text-slate-600 dark:text-slate-400 hover:text-emerald-600 dark:hover:text-emerald-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-md transition-colors cursor-pointer"
                title="Zoom Out"
              >
                <ZoomOut className="w-4 h-4" />
              </button>
              <span className="text-xs font-semibold text-slate-700 dark:text-slate-300 w-12 text-center pointer-events-none select-none">
                {Math.round(zoom * 100)}%
              </span>
              <button
                onClick={() => setZoom(z => Math.min(2, z + 0.25))}
                className="p-1 text-slate-600 dark:text-slate-400 hover:text-emerald-600 dark:hover:text-emerald-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-md transition-colors cursor-pointer"
                title="Zoom In"
              >
                <ZoomIn className="w-4 h-4" />
              </button>
            </div>
          )}
          {isGenerating ? (
            <div className="flex flex-col items-center justify-center h-full gap-3 text-emerald-600 dark:text-emerald-500">
              <Loader2 className="w-8 h-8 animate-spin" />
              <span className="text-sm font-medium">Refining Resume...</span>
            </div>
          ) : pdfBlob ? (
            <Document
              file={pdfBlob}
              onLoadSuccess={({ numPages }) => setNumPages(numPages)}
              loading={<Loader2 className="w-8 h-8 animate-spin text-emerald-500 mt-10" />}
              className="flex flex-col gap-6 mx-auto items-center origin-top w-fit pb-10"
            >
              {Array.from(new Array(numPages), (el, index) => (
                <div key={`page_${index + 1}`}>
                  <Page
                    pageNumber={index + 1}
                    className="dark:shadow-md bg-white pdf-page-container"
                    width={containerWidth}
                    scale={zoom}
                    renderTextLayer={true}
                    renderAnnotationLayer={true}
                  />
                </div>
              ))}
            </Document>
          ) : resumeData ? (
            <div className="flex flex-col items-center justify-center h-full gap-3 text-slate-500">
              <Loader2 className="w-8 h-8 animate-spin" />
              <span className="text-sm font-medium">Rendering PDF...</span>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
};
